import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Link,
  Stack,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '@/common/auth/hooks/useAuth';
import { LoadingState } from '@/common/components/loading';
import { Mono, useModeTokens } from '@/common/components/ui';
import { formatFileSize } from '@/common/utils/fileUtils';
import { DAWType } from '@/types/daw';
import { GetProjectDocument, GetProjectsDocument } from '@/generated/graphql';
import { GET_PROJECT_PREVIEW } from '@/graphql/projects';
import type { DawSaveAction } from '@/types/electron';
import {
  ProjectDetailsProvider,
  useProjectDetails,
} from '@/common/components/projectDetails/context/ProjectDetailsContext';
import { FileStatusProvider } from '@/common/components/projectDetails/context/FileStatusContext';
import ProjectHeader from '@/common/components/projectDetails/components/ProjectHeader';
import ProjectPreviewBanner from '@/common/components/projectDetails/components/ProjectPreviewBanner';
import ProjectVersionRail from '@/common/components/projectDetails/components/ProjectVersionRail';
import ProjectOverviewTab from '@/common/components/projectDetails/tabs/ProjectOverviewTab';
import FileExplorerTab from '@/common/components/projectDetails/tabs/FileExplorerTab';
import ProjectMembersTab from '@/common/components/projectDetails/tabs/ProjectMembersTab';
import ProjectSyncTab from '@/common/components/projectDetails/tabs/ProjectSyncTab';
import PluginsTab from '@/common/components/projectDetails/tabs/PluginsTab';
import AppFooter from '@/common/components/layout/AppFooter';

const tabs = [
  'Overview',
  'Versions',
  'Files',
  'Members',
  'Sync',
  'Plugins',
] as const;

function ProjectPageContent() {
  const t = useModeTokens();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    projectId,
    project,
    projectLoading,
    projectError,
    refetchProject,
    primaryDAW,
    latestVersionFiles,
    latestVersionLoading,
    latestVersionError,
    latestVersionNumber,
    selectedVersion,
    filesWithSyncRef,
  } = useProjectDetails();
  const { userId, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const saveAction: DawSaveAction | undefined = location.state?.dawSaveAction;
  const autoCommitMessage =
    saveAction?.kind === 'version' && saveAction.projectId === projectId
      ? saveAction.commitMessage
      : undefined;
  const consumeSaveAction = useCallback(() => {
    const state = { ...location.state };
    delete state.dawSaveAction;
    delete state.receivedAt;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state,
    });
  }, [location, navigate]);
  const requestedFrom: unknown = location.state?.from;
  const returnTo =
    typeof requestedFrom === 'string' &&
    (requestedFrom === '/projects' ||
      /^\/boards(?:\/[^/?#]+)?(?:\?.*)?$/.test(requestedFrom))
      ? requestedFrom
      : '/projects';
  const leaveProject = () => navigate(returnTo);
  const availableTabs = tabs.filter(
    (tab) =>
      (tab !== 'Versions' || isMobile) &&
      (tab !== 'Plugins' || primaryDAW?.type !== DAWType.STEMS)
  );
  const requestedTab = searchParams.get('tab') || 'overview';
  const activeTab = availableTabs.some(
    (tab) => tab.toLowerCase() === requestedTab
  )
    ? requestedTab
    : 'overview';

  if (projectLoading && !project)
    return (
      <Box sx={{ p: 4 }}>
        <LoadingState message="Loading project…" minHeight={400} />
      </Box>
    );
  if (projectError || !project)
    return (
      <Stack gap={2} sx={{ p: 4 }}>
        <Button
          onClick={leaveProject}
          startIcon={<ArrowBack />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back
        </Button>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => refetchProject()}>
              Retry
            </Button>
          }
        >
          Could not load this project. It may be unavailable or you may not have
          access.
        </Alert>
      </Stack>
    );

  return (
    <Box
      sx={{
        bgcolor: t.surfaces.canvas,
        minWidth: 0,
        height: { md: '100%' },
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: '340px minmax(0, 1fr)',
          xl: '360px minmax(0, 1fr)',
        },
      }}
    >
      {!isMobile && <ProjectVersionRail />}
      <Box
        role="region"
        aria-label="Project workspace"
        tabIndex={0}
        sx={{
          minWidth: 0,
          minHeight: 0,
          overflowY: { md: 'auto' },
          overscrollBehaviorY: 'contain',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }}
      >
        <Box
          component="header"
          sx={{
            px: { xs: 2, sm: 3, lg: 4 },
            pt: 2.5,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
            sx={{ mb: 2.5 }}
          >
            <Breadcrumbs aria-label="Project navigation">
              <Link
                component={RouterLink}
                to={returnTo}
                color="text.secondary"
                underline="hover"
                sx={{ fontSize: 13 }}
              >
                {returnTo.startsWith('/boards') ? 'Back to board' : 'Projects'}
              </Link>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {project.name}
              </Typography>
            </Breadcrumbs>
          </Stack>
          <Typography
            component="h1"
            variant="h2"
            sx={{ mb: 1, overflowWrap: 'anywhere' }}
          >
            {project.name}
          </Typography>
          <Mono size={11} sx={{ display: 'block', mb: 2.5 }}>
            {project.versionHistory.length}{' '}
            {project.versionHistory.length === 1 ? 'VERSION' : 'VERSIONS'}
            {' · '}
            {latestVersionLoading ? (
              <Skeleton
                component="span"
                width={190}
                sx={{ display: 'inline-block' }}
              />
            ) : latestVersionError ? (
              'File statistics unavailable'
            ) : (
              <>
                LATEST · {latestVersionFiles.length} FILES ·{' '}
                {formatFileSize(
                  latestVersionFiles.reduce(
                    (total, file) => total + file.size,
                    0
                  )
                )}
              </>
            )}
          </Mono>
          <ProjectHeader
            autoCommitMessage={autoCommitMessage}
            onAutoCommitStarted={consumeSaveAction}
          />
          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'baseline',
            }}
            role="status"
          >
            <Typography variant="body2" fontWeight={600}>
              Version {selectedVersion}
              {selectedVersion === latestVersionNumber ? ' · Latest' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Preview, files and plugins follow this version.
            </Typography>
          </Box>
          <ProjectPreviewBanner />
        </Box>
        <Box
          sx={{
            minHeight: 480,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                px: { xs: 1, sm: 2 },
                borderBottom: 1,
                borderColor: 'divider',
                position: 'sticky',
                top: { xs: 56, md: 0 },
                zIndex: 1,
                bgcolor: t.surfaces.canvas,
              }}
            >
              <Tabs
                value={activeTab}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Project details"
                sx={{ minHeight: 56 }}
                onChange={(_, value: string) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('tab', value);
                  setSearchParams(next, { state: location.state });
                }}
              >
                {availableTabs.map((tab) => (
                  <Tab
                    key={tab}
                    label={tab}
                    value={tab.toLowerCase()}
                    data-cy={tab}
                    id={`page-tab-${tab.toLowerCase()}`}
                    aria-controls={`page-panel-${tab.toLowerCase()}`}
                    sx={{ minHeight: 56 }}
                  />
                ))}
              </Tabs>
            </Box>
            <Box
              role="tabpanel"
              id={`page-panel-${activeTab}`}
              aria-labelledby={`page-tab-${activeTab}`}
              sx={{
                p: activeTab === 'versions' ? 0 : { xs: 2, sm: 3, lg: 4 },
                minWidth: 0,
              }}
            >
              {(activeTab === 'files' || activeTab === 'plugins') && (
                <Mono size={12} sx={{ display: 'block', mb: 2 }}>
                  Viewing version {selectedVersion}
                </Mono>
              )}
              {activeTab === 'overview' && (
                <ProjectOverviewTab onClose={leaveProject} />
              )}
              {activeTab === 'versions' && <ProjectVersionRail variant="tab" />}
              {activeTab === 'files' && (
                <FileExplorerTab ref={filesWithSyncRef} />
              )}
              {activeTab === 'members' && isAuthenticated && userId && (
                <ProjectMembersTab onClose={leaveProject} />
              )}
              {activeTab === 'sync' && <ProjectSyncTab />}
              {activeTab === 'plugins' && <PluginsTab />}
            </Box>
          </Box>
        </Box>
        <AppFooter />
      </Box>
    </Box>
  );
}

export default function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const client = useApolloClient();
  const onProjectUpdated = useCallback(() => {
    // Refresh the collection even while its route is unmounted.
    void Promise.all([
      client.refetchQueries({
        include: [GetProjectDocument, GET_PROJECT_PREVIEW],
      }),
      client.query({ query: GetProjectsDocument, fetchPolicy: 'network-only' }),
    ]).catch((error: unknown) =>
      console.error('Failed to refresh project data:', error)
    );
  }, [client]);
  if (!projectId) return null;

  return (
    <ProjectDetailsProvider
      key={projectId}
      projectId={projectId}
      onProjectUpdated={onProjectUpdated}
    >
      <FileStatusProvider>
        <ProjectPageContent />
      </FileStatusProvider>
    </ProjectDetailsProvider>
  );
}
